//Libraries
import { useState } from 'react';
import { ArrowRight, Button, CheckMark, Dialog, EyeClosed, Field, IconButton, ViewEye, XMarkExit } from '@effizency/efz-design-system';
import { useZodForm } from '@effizency/efz-toolkit/hooks';
import { Controller } from 'react-hook-form';

//Components
import Alert from 'components/@efz/Alert';
import IntlMessages from 'components/util/IntlMessages';

//Hooks
import useRecoveryPassword from 'hooks/recoveryPassword';

//Schemas
import { redefinePasswordFormSchema } from 'schemas/forms/auth';

//Services
import { getFormErrorMessage, intlMessages, PASSWORD_CHECK, PASSWORD_MIN_LENGTH } from 'services/util/auxiliaryUtils';

//Stores
import { useUserStore } from 'stores/user';

import type { TRedefinePasswordFormValues } from 'types/forms/auth';

import { StyledPasswordBullet, StyledPasswordBullets, StyledRedefinePasswordForm } from './index.styled';

const FORM_ID = 'redefine-password-form';

// The rules the info box lists, in its order (dialog.passwordRedefine.info.part1 to part5).
const PASSWORD_BULLETS = [
    (password: string) => password.length >= PASSWORD_MIN_LENGTH,
    (password: string) => PASSWORD_CHECK.PASSWORD_HAS_DIGIT_REGEXP.test(password),
    (password: string) => PASSWORD_CHECK.PASSWORD_HAS_UPPERCASE_REGEXP.test(password),
    (password: string) => PASSWORD_CHECK.PASSWORD_HAS_LOWERCASE_REGEXP.test(password),
    (password: string) => PASSWORD_CHECK.PASSWORD_HAS_SPECIAL_CHARACTER.test(password),
];

/** Asks a user who has not changed the password they were given for a new one. It only closes once it is saved. */
const RedefinePasswordDialog = () => {
    const {
        user,
        actions: { logout },
    } = useUserStore();

    const [redefinePasswordOpen, setRedefinePasswordOpen] = useState(true);
    const [showPassword, setShowPassword] = useState(false);

    const { isRequestingRedefinePwd, fetchRedefinePassword } = useRecoveryPassword();

    const {
        handleSubmit,
        control,
        watch,
        formState: { errors },
    } = useZodForm({
        schema: redefinePasswordFormSchema,
        mode: 'onChange',
        defaultValues: { newPassword: '', confirmPassword: '' },
    });

    const watchNewPassword = watch('newPassword') ?? '';

    const onSubmitClickHandler = ({ newPassword }: TRedefinePasswordFormValues) => {
        fetchRedefinePassword(logout, user.id, newPassword, () => setRedefinePasswordOpen(false));
    };

    const visibilityButton = (
        <IconButton
            variant="tertiary"
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword(!showPassword)}
            dataTestId="redefine-password-visibility"
        >
            {showPassword ?
                <ViewEye />
            :   <EyeClosed />}
        </IconButton>
    );

    const passwordBullets = (
        <StyledPasswordBullets>
            {PASSWORD_BULLETS.map((isMet, index) => {
                const state =
                    watchNewPassword === '' ? 'pending'
                    : isMet(watchNewPassword) ? 'met'
                    : 'unmet';
                return (
                    <StyledPasswordBullet key={`dialog-bullet-${index + 1}`} $state={state}>
                        {state === 'pending' ?
                            <ArrowRight />
                        : state === 'met' ?
                            <CheckMark />
                        :   <XMarkExit />}
                        <IntlMessages id={`dialog.passwordRedefine.info.part${index + 1}`} />
                    </StyledPasswordBullet>
                );
            })}
        </StyledPasswordBullets>
    );

    return (
        <Dialog
            open={redefinePasswordOpen}
            title={<IntlMessages id="dialog.passwordRedefine.title" />}
            maxWidth="md"
            dataTestId="redefine-password-dialog"
            primaryButton={
                <Button
                    type="submit"
                    form={FORM_ID}
                    disabled={isRequestingRedefinePwd}
                    loading={isRequestingRedefinePwd}
                    dataTestId="redefine-password-send"
                >
                    <IntlMessages id="dialog.passwordRedefine.button.send" />
                </Button>
            }
        >
            <StyledRedefinePasswordForm id={FORM_ID} onSubmit={handleSubmit(onSubmitClickHandler)}>
                <div className="redefine-password-fields">
                    {/* newPassword */}
                    <Controller
                        name="newPassword"
                        control={control}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Field
                                {...field}
                                value={field.value ?? ''}
                                type={showPassword ? 'text' : 'password'}
                                size="md"
                                maxWidth="full"
                                label={<IntlMessages id="dialog.passwordRedefine.label.newPassword" />}
                                required
                                placeholder={intlMessages('dialog.passwordRedefine.label.newPassword')}
                                disabled={isRequestingRedefinePwd}
                                error={!!errors?.newPassword}
                                errorMessage={getFormErrorMessage(errors?.newPassword, null, { min: PASSWORD_MIN_LENGTH })}
                                endAdornment={visibilityButton}
                                dataTestId="redefine-password-new"
                            />
                        )}
                    />

                    {/* confirmPassword */}
                    <Controller
                        name="confirmPassword"
                        control={control}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Field
                                {...field}
                                value={field.value ?? ''}
                                type={showPassword ? 'text' : 'password'}
                                size="md"
                                maxWidth="full"
                                label={<IntlMessages id="dialog.passwordRedefine.label.confirmPassword" />}
                                required
                                placeholder={intlMessages('dialog.passwordRedefine.label.confirmPassword')}
                                disabled={isRequestingRedefinePwd}
                                error={!!errors?.confirmPassword}
                                errorMessage={getFormErrorMessage(errors?.confirmPassword)}
                                endAdornment={visibilityButton}
                                dataTestId="redefine-password-confirm"
                            />
                        )}
                    />
                </div>

                <Alert severity="info" IntlMessages={<IntlMessages id="dialog.passwordRedefine.info" />} secondaryText={passwordBullets} />
            </StyledRedefinePasswordForm>
        </Dialog>
    );
};

export default RedefinePasswordDialog;
